import { useEffect, useMemo } from "react";
import { Alert, Avatar, Form, Input, Select, Switch } from "antd";
import { type FormInstance } from "antd/lib/form";
import keyBy from "lodash.keyby";
import { useTranslation } from "react-i18next";

import { teamSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope, TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models";
import { getUserInitials } from "@fitness-app/utils";

import { useTagOptions } from "~/hooks/useTagOptions";
import { usePermission } from "~/hooks/usePermission";
import { useAppSelector } from "~/store/initializeStore";
import { type TeamTrainerFormModel } from "./types";

interface TeamTrainerFormProps {
  formController?: FormInstance<TeamTrainerFormModel>;
  onSubmit: (formData: TeamTrainerFormModel) => void;
  model?: Partial<TeamTrainerFormModel> | null;
}

const TeamTrainerForm = ({ model, onSubmit, formController }: TeamTrainerFormProps) => {
  const { t } = useTranslation(["team", "common"]);
  const { options: tagsOptions } = useTagOptions("specializationTags");
  const selectedRole = Form.useWatch("role", formController);
  const onlyNotAssignedTrainers = Form.useWatch("showOnlyNotAssigned", formController);
  const chatAgentEnabled = Form.useWatch<boolean>(["chatAgentConfig", "enabled"], formController);
  const chatAgentMode = Form.useWatch<"automatic" | "manual">(["chatAgentConfig", "mode"], formController);
  const trainers = useAppSelector(teamSelectors.getTrainersTeam);
  const assignedTrainers = useAppSelector(teamSelectors.getAssignedRegularTrainers);
  const canManageMembership = usePermission(PermissionKey.TEAM_PERMISSIONS_MANAGE, PermissionScope.ALL);

  useEffect(() => {
    if (model) {
      formController?.setFieldsValue(model);
    }
  }, [model, formController]);

  const editMode = Boolean(model);
  const isTrainerRole =
    selectedRole === TeamMemberRole.REGULAR_TRAINER || selectedRole === TeamMemberRole.SHARED_TRAINER;
  const canConfigureChatAgent = editMode && isTrainerRole;
  const isEligibleForChatAgent = Boolean(model?.userId) && model?.status === true;
  const isAutomaticChatAgentMode = chatAgentMode !== "manual";

  useEffect(() => {
    if (!canConfigureChatAgent && chatAgentEnabled) {
      formController?.setFieldValue(["chatAgentConfig", "enabled"], false);
    }
  }, [canConfigureChatAgent, chatAgentEnabled, formController]);

  const currentTrainerId = model?.id;

  const trainerOptions = useMemo(() => {
    const byId = keyBy(trainers, "id");

    const getAssignedTo = (trainerId: string) =>
      Object.keys(assignedTrainers).find((assignedTrainerId) =>
        assignedTrainers[assignedTrainerId]?.includes(trainerId),
      );

    const list = trainers
      .filter(
        (trainer) =>
          trainer.role === TeamMemberRole.REGULAR_TRAINER &&
          [TeamMemberStatus.ACTIVE, TeamMemberStatus.INVITED].includes(trainer.status),
      )
      .map((trainer) => {
        const assignedTo = getAssignedTo(trainer.id);
        const assignedTrainerData = assignedTo ? byId[assignedTo] : null;

        return {
          label: `${trainer.firstName} ${trainer.lastName}`,
          value: trainer.id,
          avatarUrl: trainer.user?.avatarUrl || null,
          assignedTo: assignedTrainerData
            ? `${assignedTrainerData.firstName.slice(0, 1)}. ${assignedTrainerData.lastName}`
            : null,
        };
      });

    if (onlyNotAssignedTrainers) {
      const withoutCurrentTrainer = {
        ...assignedTrainers,
        [currentTrainerId || "none"]: [],
      };

      const assignedTrainersIds = Object.values(withoutCurrentTrainer).flat();

      return list.filter((trainer) => !assignedTrainersIds.includes(trainer.value));
    }

    return list;
  }, [trainers, assignedTrainers, onlyNotAssignedTrainers, currentTrainerId]);

  return (
    <Form<TeamTrainerFormModel>
      name="form"
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 14 }}
      layout="horizontal"
      form={formController}
      initialValues={{
        firstName: "",
        lastName: "",
        email: "",
        status: true,
        role: null,
        specializationTags: [],
        showOnlyNotAssigned: true,
        chatAgentConfig: {
          enabled: false,
          mode: "automatic",
          replyMode: "suggestion",
          sensitivity: "balanced",
          tone: "",
          writingStyle: "",
          additionalInstructions: "",
        },
      }}
      onFinish={onSubmit}
    >
      <Form.Item
        name="firstName"
        label={t<string>("common.firstName")}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <Input disabled={editMode} />
      </Form.Item>
      <Form.Item
        name="lastName"
        label={t<string>("common.lastName")}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <Input disabled={editMode} />
      </Form.Item>
      <Form.Item
        name="email"
        label={t<string>("common.email")}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
          {
            type: "email",
            message: t<string>("common:validationErrors.wrongEmail"),
          },
        ]}
      >
        <Input disabled={editMode} />
      </Form.Item>

      <Form.Item
        name="role"
        label={t<string>("common.role")}
        rules={[
          {
            required: true,
            message: t<string>("common:validationErrors.fieldIsRequired"),
          },
        ]}
      >
        <Select
          disabled={editMode && !canManageMembership}
          placeholder={t<string>("selectRole")}
          options={[
            {
              label: t("role.regular_trainer"),
              value: TeamMemberRole.REGULAR_TRAINER,
            },
            {
              label: t("role.shared_trainer"),
              value: TeamMemberRole.SHARED_TRAINER,
            },
            {
              label: t("role.administration"),
              value: TeamMemberRole.ADMINISTRATION,
            },
            {
              label: t("role.sales_rep"),
              value: TeamMemberRole.SALES_REP,
            },
            {
              label: t("role.support"),
              value: TeamMemberRole.SUPPORT,
            },
          ]}
        />
      </Form.Item>

      <Form.Item name="specializationTags" label={t<string>("membersSpecializations")}>
        <Select notFoundContent={false} options={tagsOptions} mode="tags" />
      </Form.Item>

      <Form.Item name="status" label={t<string>("common.active")} valuePropName="checked">
        <Switch disabled={!model || !canManageMembership} />
      </Form.Item>

      <Form.Item
        name="calendlyEmail"
        label={t<string>("common.calendlyEmail")}
        tooltip={t<string>("common.calendlyEmailTooltip")}
        rules={[
          {
            type: "email",
            message: t<string>("common:validationErrors.wrongEmail"),
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item name="slackId" label={t<string>("common.slackId")}>
        <Input />
      </Form.Item>

      {canConfigureChatAgent ? (
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="mb-4 text-sm font-semibold">{t("chatAgent.title")}</div>

          {!isEligibleForChatAgent ? (
            <Alert className="mb-4" type="info" showIcon message={t("chatAgent.eligibilityHint")} />
          ) : null}

          <Form.Item name={["chatAgentConfig", "enabled"]} label={t("chatAgent.enabled")} valuePropName="checked">
            <Switch disabled={!isEligibleForChatAgent && !chatAgentEnabled} />
          </Form.Item>

          <Form.Item name={["chatAgentConfig", "mode"]} label={t("chatAgent.mode")}>
            <Select
              disabled={!chatAgentEnabled || !isEligibleForChatAgent}
              options={[
                {
                  label: t("chatAgent.modeAutomatic"),
                  value: "automatic",
                },
                {
                  label: t("chatAgent.modeManual"),
                  value: "manual",
                },
              ]}
            />
          </Form.Item>

          {chatAgentEnabled && isEligibleForChatAgent && isAutomaticChatAgentMode ? (
            <Alert className="mb-4" type="info" showIcon message={t("chatAgent.automaticHint")} />
          ) : null}

          <Form.Item name={["chatAgentConfig", "replyMode"]} label={t("chatAgent.replyMode")}>
            <Select
              disabled={!chatAgentEnabled || !isEligibleForChatAgent}
              options={[
                {
                  label: t("chatAgent.replySuggestion"),
                  value: "suggestion",
                },
                {
                  label: t("chatAgent.replyAutomatic"),
                  value: "automatic",
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            name={["chatAgentConfig", "sensitivity"]}
            label={t("chatAgent.sensitivity")}
            tooltip={t<string>("chatAgent.sensitivityTooltip")}
          >
            <Select
              disabled={!chatAgentEnabled || !isEligibleForChatAgent}
              options={[
                {
                  label: t("chatAgent.sensitivityLow"),
                  value: "low",
                },
                {
                  label: t("chatAgent.sensitivityBalanced"),
                  value: "balanced",
                },
                {
                  label: t("chatAgent.sensitivityHigh"),
                  value: "high",
                },
              ]}
            />
          </Form.Item>

          <Form.Item name={["chatAgentConfig", "tone"]} label={t("chatAgent.tone")}>
            <Input disabled={!chatAgentEnabled || !isEligibleForChatAgent || isAutomaticChatAgentMode} />
          </Form.Item>

          <Form.Item name={["chatAgentConfig", "writingStyle"]} label={t("chatAgent.writingStyle")}>
            <Input disabled={!chatAgentEnabled || !isEligibleForChatAgent || isAutomaticChatAgentMode} />
          </Form.Item>

          <Form.Item name={["chatAgentConfig", "additionalInstructions"]} label={t("chatAgent.additionalInstructions")}>
            <Input.TextArea
              rows={3}
              disabled={!chatAgentEnabled || !isEligibleForChatAgent || isAutomaticChatAgentMode}
            />
          </Form.Item>
        </div>
      ) : null}

      {selectedRole === TeamMemberRole.SHARED_TRAINER ? (
        <div>
          <Form.Item name="showOnlyNotAssigned" label={t<string>("common.showOnlyNotAssigned")} valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item name="assignedTrainers" label={t<string>("common.assignedTrainers")}>
            <Select
              showSearch
              optionFilterProp="label"
              optionLabelProp="label"
              mode="multiple"
              placeholder={t("common.selectTrainers")}
            >
              {trainerOptions.map((trainer) => (
                <Select.Option key={trainer.value} value={trainer.value} title={trainer.label} label={trainer.label}>
                  <Avatar src={trainer.avatarUrl}>{getUserInitials(trainer.label)}</Avatar> {trainer.label}{" "}
                  {trainer.assignedTo ? `(${trainer.assignedTo})` : ""}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      ) : null}
    </Form>
  );
};

export default TeamTrainerForm;
